import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { app_logo_full } from "@/constants";

const privacyPolicyData = [
  {
    heading: "1. PURPOSE",
    data: [
      "This policy defines and outlines Riffs overall privacy guidelines.",
      "Riffs Inc respects the privacy of every individual. In this policy, we describe our approach to privacy, as well as how we collect, store, use, and protect individual information. Additionally, it outlines the right to access the personal information that we collect and maintain. ",
    ],
  },
  {
    heading: "2. SCOPE",
    data: [
      "This privacy policy outlines the scope of programs and controls required to ensure “privacy by design” through governance principles. The privacy program details the security we apply on sensitive personal identifiable information (PII).",
    ],
  },
  {
    heading: "3. ACRONYMS & DEFINITIONS",
    data: ["The acronyms and definitions used in this policy are:"],
    subheading: [
      {
        heading: "Data subjects",
        data:
          "Individuals who can be identified, directly or indirectly, by data. Data may be a name, an identification number, or any other factor relating to their physical, physiological, genetic, psychological, economic, cultural, or social identity.",
      },
      {
        heading: "Data Processing",
        data:
          "Any operation or set of operations performed on personal data or sets of personal data. Processing can be automated, such as collection, recording, organization, structuring, storage, adaptation or alteration, retrieval, consultation, use, disclosure by transmission, dissemination or otherwise made available, alignment or combination, restriction, erasure, or destruction.",
      },
      {
        heading: "Data Controller",
        data:
          "The entity that, alone or jointly with others, determines the purposes and means of personal data processing.",
      },
    ],
  },
  {
    heading: "4. PERSONAL DATA PROCESSING PRINCIPLES",
    data: [
      "There are several fundamental principles upon which privacy is based. Riffs processes personal data based on the following principles: ",
    ],
    points: [
      "Processed lawfully, fairly and in a transparent manner in relation to the data subject (‘lawfulness, fairness and transparency’)",
      "Collected for specified, explicit and legitimate purposes and not further processed in amanner that is incompatible with those purposes (‘purpose limitation’).",
      "Collect and process data adequately, relevant and limited to what is necessary in relation to the purposes for which they are processed (‘data minimization’)",
      "Process personal data accurately and, where necessary, kept up to date; every reasonable step will be taken to ensure that personal data that are inaccurate, having regard to the purposes for which they are processed, will be erased, or rectified (‘accuracy’).",
      "Kept in a form which permits identification of data subjects for no longer than is necessary for the purposes for which the personal data are processed (‘storage limitation’).",
      "Processed in a manner that ensures appropriate security of the personal data, including protection against unauthorized or unlawful processing and against accidental loss, destruction, or damage, using appropriate technical or organizational measures (‘integrity and confidentiality’).",
    ],
    endText: [
      "It is under the responsibility of Riffs’s CISO to ensure that personal data processing complies with all these principles both in the processing it currently carries out and as part of the introduction of new methods of processing such as new IT systems.",
    ],
  },
  {
    heading: "5. CONSENT",
    data: [
      "Wherever possible, explicit consent will be obtained from a data subject to collect and process their data. Transparent information about the usage of their personal data will be provided at the time that consent is obtained including their right to withdraw consent (opt out). This information must be provided in an accessible form, written in clear language and free of charge.",
    ],
  },
  {
    heading: "6. PRIVACY BY DESIGN",
    data: [
      "Riffs has adopted the principle of privacy by design and will ensure that the definition and planning of all new or significantly changed systems that collect, or process personal data will be subject to due consideration of privacy issues.",
      "New system will be designed under the consideration of the following:",
    ],
    points: [
      "How personal data will be processed and for what purposes.",
      "Is the proposed processing of personal data is both necessary and proportionate to the purpose(s)?",
      "What are the risks to individuals in processing personal data?",
      "What controls are necessary to address?",
      "How to demonstrate compliance with privacy relevant legislation?",
    ],
    endText: [
      "Use of techniques such as data minimization and pseudonymization will be considered where applicable and appropriate.",
    ],
  },
  {
    heading: "7. DATA PROTECTION OFFICER",
    data: [
      "To ensure that privacy principles are implemented, Riffs will designate a Data Protection Officer (DPO). The DPO will have managerial responsibility and will ensure the implementation of the principles outlined within this policy.",
    ],
  },
  {
    heading: "8. BREACH NOTIFICATION",
    data: [
      "Riffs DPO will be responsible for acting on and inform affected parties regarding any breaches of personal data. In line with privacy legislations, relevant supervisory authority will be informed within 72 hours. This will be managed in accordance with Riffs Information Security Incident Response Procedure which sets out the overall process of handling information security incidents.",
    ],
  },
  {
    heading: "PRIVACY OBLIGATIONS AS A CLOUD-BASED SERVICE PROVIDER",
    data: [
      "As Riffs stores and processes personal data coming from cloud customers, the following controls will be enabled:",
    ],
    points: [
      "Riffs will provide customers with the facilities to meet their obligations under law in activities such as accessing, amending, and erasing individuals’ PII.",
      "Cloud customers’ personal data usage will be restricted to their purposes. ",
      "Customer will be informed if their data needs to be disclosed, as required by law, unless Riffs will be prohibited from doing so",
      "Details of disclosures will be recorded.",
      "Customers will be told if subcontractors are requested to process their personal data.",
      "Customers will be told if their personal data experienced unauthorized access.",
      "Customers will be informed in which country or countries their personal data is stored.",
    ],
    endText: [
      "Wherever applicable, stored personal data will be anonymized and or encrypted.",
    ],
  },
];

const Privacy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <Box>
      <Stack
        spacing={3}
        height={"70vh"}
        textAlign="center"
        alignItems="center"
        justifyContent="center"
        bgcolor="#0b494b"
        padding="0px 10px"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src={app_logo_full} alt="" width={98} />
        </Box>
        <Typography
          variant="h2"
          color="#FFF"
          sx={{ fontSize: { xs: "25px", sm: "32px", md: "36px" } }}
        >
          Privacy and Data Protection Policy
        </Typography>
        <Typography variant="h6" color="#FFF">Last updated: April 04, 2024</Typography>
        <Box sx={{ display: "flex", pt: 6, alignItems: "center", gap: 2 }}>
          <Button
            variant={location.pathname === "/privacy" ? "contained" : "outlined"}
            size="large"
            sx={{
              width: "160px",
              borderRadius: "8px",
              backgroundColor:
                location.pathname === "/privacy" ? "#E0F7F9" : "transparent",
              color: location.pathname === "/privacy" ? "#0b494b" : "#CFD8DC",
              borderColor: location.pathname === "/privacy" ? "none" : "#CFD8DC",
              "&:hover": {
                backgroundColor:
                  location.pathname === "/privacy" ? "#B2EBF2" : "rgba(38, 198, 218, 0.1)",
                color: location.pathname === "/privacy" ? "#0b494b" : "#26C6DA",
                borderColor: location.pathname === "/privacy" ? "none" : "#26C6DA",
              },
            }}
            onClick={() => navigate("/privacy")}
          >
            Privacy
          </Button>
          <Button
            variant={location.pathname === "/terms" ? "contained" : "outlined"}
            size="large"
            sx={{
              width: "160px",
              borderRadius: "8px",
              backgroundColor:
                location.pathname === "/terms" ? "#E0F7F9" : "transparent",
              color: location.pathname === "/terms" ? "#0b494b" : "#CFD8DC",
              borderColor: location.pathname === "/terms" ? "none" : "#CFD8DC",
              "&:hover": {
                backgroundColor:
                  location.pathname === "/terms" ? "#B2EBF2" : "rgba(38, 198, 218, 0.1)",
                color: location.pathname === "/terms" ? "#0b494b" : "#26C6DA",
                borderColor: location.pathname === "/terms" ? "none" : "#26C6DA",
              },
            }}
            onClick={() => navigate("/terms")}
          >
            Terms
          </Button>
        </Box>
      </Stack>

      <Container maxWidth="md" sx={{ pt: 6, pb: 6 }}>
        {privacyPolicyData.map((section, index) => (
          <Box key={index} sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "bold",
                borderBottom: "1px solid black",
                fontSize: { xs: "18px", sm: "22px", md: "24px" },
              }}
            >
              {section.heading}
            </Typography>

            {section.data.map((item, idx) => (
              <Box key={idx} sx={{ mb: 1, pt: 1 }}>
                <Typography variant="body1" lineHeight={1.8}>
                  {item}
                </Typography>
              </Box>
            ))}

            {section.subheading &&
              section.subheading.map((subItem, subIdx) => (
                <List key={subIdx} sx={{ pl: 4 }}>
                  <ListItem
                    sx={{ display: "list-item", listStyleType: "disc" }}
                  >
                    <Typography variant="body1" lineHeight={1.6}>
                      <strong style={{ fontSize: "18px" }}>
                        {subItem.heading} -{" "}
                      </strong>
                      {subItem.data}
                    </Typography>
                  </ListItem>
                </List>
              ))}

            {section.points && (
              <List sx={{ pl: 4 }}>
                {section.points.map((subItem, subIdx) => (
                  <ListItem
                    key={subIdx}
                    sx={{
                      display: "list-item",
                      listStyleType: "decimal",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body1" lineHeight={1.6}>
                      {subItem}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}

            {section.endText &&
              section.endText.map((item, idx) => (
                <Box key={idx} sx={{ mb: 1, pt: 1 }}>
                  <Typography variant="body1" lineHeight={1.8}>
                    {item}
                  </Typography>
                </Box>
              ))}
          </Box>
        ))}
      </Container>
    </Box>
  );
};

export default Privacy;
